import { useRef, useCallback } from 'react';
import { ModifyObjectCommand } from '../commands/ModifyObjectCommand';
import { snapValue } from '../components/Editor/tools/snapUtils';
import {
  localToWorld,
  createVertexHandle,
  updatePolygonPoint,
  positionHandlesFromPolygon,
  isVertexHandle,
} from '../components/Editor/tools/vertexEditUtils';

/**
 * useVertexEditing - React hook for polygon vertex edit mode
 *
 * Manages:
 * - Entering/exiting vertex edit mode on double-click
 * - Draggable vertex handles (Fabric.js Circle objects)
 * - Snap-to-grid and snap-to-endpoint during drag
 * - Self-intersection prevention
 * - Undo/redo via ModifyObjectCommand
 * - Bidirectional sync with PolygonProperties panel
 *
 * @param {React.MutableRefObject<fabric.Canvas>} canvasRef - Ref to Fabric.js canvas
 * @param {object} commandStack - Command stack for undo/redo
 * @param {React.MutableRefObject<{snapEnabled: boolean, gridSize: number}>} snapSettingsRef
 *   - Mutable ref with current snap settings (read at event time, always fresh)
 * @returns {{ editingPolygonRef, enterEditMode, exitEditMode, refreshHandles, isInEditMode }}
 */
export function useVertexEditing(canvasRef, commandStack, snapSettingsRef) {
  const editingPolygonRef = useRef(null);
  const handlesRef = useRef([]);
  // Snapshot of points at drag-start, used for undo command
  const oldPointsRef = useRef(null);
  // Saved original stroke settings to restore on exit
  const originalStrokeRef = useRef(null);
  // Tracks Ctrl key state for snap override
  const ctrlPressedRef = useRef(false);
  // Cleanup function for Ctrl key listeners
  const ctrlCleanupRef = useRef(null);
  // Listener for polygon 'modified' (e.g., after undo/redo) to reposition handles
  const polygonModifiedListenerRef = useRef(null);

  /**
   * Reposition all handles to match current polygon vertex world positions.
   * Call after polygon.points changes externally (undo/redo, properties panel).
   */
  const refreshHandles = useCallback(() => {
    const polygon = editingPolygonRef.current;
    const canvas = canvasRef.current;
    if (!polygon || !canvas || handlesRef.current.length === 0) return;

    positionHandlesFromPolygon(polygon, handlesRef.current);
    canvas.requestRenderAll();
  }, [canvasRef]);

  /**
   * Exit vertex edit mode: remove handles, restore polygon appearance.
   */
  const exitEditMode = useCallback(() => {
    const polygon = editingPolygonRef.current;
    const canvas = canvasRef.current;
    if (!polygon || !canvas) return;

    // Restore original stroke
    polygon.set({
      strokeDashArray: originalStrokeRef.current?.strokeDashArray ?? null,
      lockMovementX: false,
      lockMovementY: false,
      lockRotation: false,
      lockScalingX: false,
      lockScalingY: false,
    });

    // Remove handles from canvas (batch)
    const handlesToRemove = [...handlesRef.current];
    if (handlesToRemove.length > 0) {
      canvas.remove(...handlesToRemove);
    }
    handlesRef.current = [];

    // Remove polygon 'modified' listener (undo/redo handle refresh)
    if (polygonModifiedListenerRef.current) {
      polygon.off('modified', polygonModifiedListenerRef.current);
      polygonModifiedListenerRef.current = null;
    }

    // Remove Ctrl key tracking
    if (ctrlCleanupRef.current) {
      ctrlCleanupRef.current();
      ctrlCleanupRef.current = null;
    }

    editingPolygonRef.current = null;
    oldPointsRef.current = null;
    originalStrokeRef.current = null;

    polygon.setCoords();
    canvas.requestRenderAll();
  }, [canvasRef]);

  /**
   * Enter vertex edit mode for a polygon.
   * Creates draggable handles at each vertex.
   */
  const enterEditMode = useCallback((polygon) => {
    const canvas = canvasRef.current;
    if (!canvas || !polygon) return;

    // Already editing this polygon
    if (editingPolygonRef.current === polygon) return;

    // Exit any existing edit mode first
    if (editingPolygonRef.current) {
      exitEditMode();
    }

    editingPolygonRef.current = polygon;

    // Save original stroke for restoration on exit
    originalStrokeRef.current = {
      strokeDashArray: polygon.strokeDashArray ?? null,
    };

    // Visual feedback: dashed stroke while in edit mode
    polygon.set({
      strokeDashArray: [8, 4],
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
    });

    // Store initial points snapshot for undo/redo
    oldPointsRef.current = polygon.points.map(p => ({ x: p.x, y: p.y }));

    // Track Ctrl key for snap override
    const handleCtrlDown = (e) => {
      if (e.key === 'Control') ctrlPressedRef.current = true;
    };
    const handleCtrlUp = (e) => {
      if (e.key === 'Control') ctrlPressedRef.current = false;
    };
    window.addEventListener('keydown', handleCtrlDown);
    window.addEventListener('keyup', handleCtrlUp);
    ctrlCleanupRef.current = () => {
      window.removeEventListener('keydown', handleCtrlDown);
      window.removeEventListener('keyup', handleCtrlUp);
    };

    /**
     * Create one handle for vertex at `index` and attach drag event listeners.
     * Extracted so both initial setup and handlePolygonModified can reuse it.
     */
    const makeHandle = (index) => {
      const worldPoint = localToWorld(polygon, polygon.points[index]);
      const handle = createVertexHandle(worldPoint.x, worldPoint.y, index);

      handle.on('moving', () => {
        const { snapEnabled, gridSize } = snapSettingsRef?.current ?? {};

        // Use snapValue directly — applyHierarchicalSnap cannot be used here because
        // snapToNearestEndpoint finds the handle's own circle center (distance=0) and
        // "snaps" to the raw position, preventing grid snap from ever triggering.
        const snappedPos = {
          x: snapValue(handle.left, gridSize ?? 10, snapEnabled ?? true, ctrlPressedRef.current),
          y: snapValue(handle.top, gridSize ?? 10, snapEnabled ?? true, ctrlPressedRef.current),
        };
        handle.set({ left: snappedPos.x, top: snappedPos.y });
        handle.setCoords();

        // Apply point update (null = self-intersection, skip)
        const newPoints = updatePolygonPoint(polygon, index, snappedPos);
        if (newPoints !== null) {
          // Record old bbox min BEFORE updating points (needed for left/top compensation)
          const oldMinX = Math.min(...polygon.points.map(p => p.x));
          const oldMinY = Math.min(...polygon.points.map(p => p.y));

          polygon.set({ points: newPoints });

          // Recompute bbox from new points
          const newMinX = Math.min(...newPoints.map(p => p.x));
          const newMinY = Math.min(...newPoints.map(p => p.y));
          const newMaxX = Math.max(...newPoints.map(p => p.x));
          const newMaxY = Math.max(...newPoints.map(p => p.y));

          // Update width/height/pathOffset so the polygon renders correctly when a
          // vertex moves outside the original bounding box.
          // Also shift left/top by the same delta as the bbox min so the center
          // (left + width/2) stays consistent and all other vertex world positions
          // remain unchanged.
          polygon.set({
            width: newMaxX - newMinX,
            height: newMaxY - newMinY,
            pathOffset: { x: (newMinX + newMaxX) / 2, y: (newMinY + newMaxY) / 2 },
            left: polygon.left + (newMinX - oldMinX),
            top: polygon.top + (newMinY - oldMinY),
          });

          polygon.setCoords();
          canvas.requestRenderAll();
        }
      });

      handle.on('modified', () => {
        // Commit undo command after drag ends
        if (oldPointsRef.current && commandStack) {
          const currentPoints = polygon.points.map(p => ({ x: p.x, y: p.y }));
          const command = new ModifyObjectCommand(
            polygon,
            { points: oldPointsRef.current },
            { points: currentPoints }
          );
          commandStack.execute(command);
          // Update snapshot for next drag
          oldPointsRef.current = currentPoints;
        }

        // Notify PolygonProperties panel to refresh vertex list
        polygon.fire('modified');
      });

      return handle;
    };

    // Create all handles (batch add)
    canvas.renderOnAddRemove = false;
    const handles = polygon.points.map((_, index) => makeHandle(index));
    handles.forEach(h => canvas.add(h));
    canvas.renderOnAddRemove = true;
    handlesRef.current = handles;

    /**
     * Called when polygon.points change externally (undo/redo, properties panel).
     * - Syncs handle count (adds/removes handles to match polygon.points.length)
     * - Updates bbox (width/height/pathOffset/left/top) from the new points
     * - Repositions all handles to their new world positions
     */
    const handlePolygonModified = () => {
      const pts = polygon.points;

      // Derive old bbox min from stale pathOffset/width/height (set before points changed)
      const oldMinX = polygon.pathOffset.x - polygon.width / 2;
      const oldMinY = polygon.pathOffset.y - polygon.height / 2;
      const newMinX = Math.min(...pts.map(p => p.x));
      const newMinY = Math.min(...pts.map(p => p.y));
      const newMaxX = Math.max(...pts.map(p => p.x));
      const newMaxY = Math.max(...pts.map(p => p.y));

      polygon.set({
        width: newMaxX - newMinX,
        height: newMaxY - newMinY,
        pathOffset: { x: (newMinX + newMaxX) / 2, y: (newMinY + newMaxY) / 2 },
        left: polygon.left + (newMinX - oldMinX),
        top: polygon.top + (newMinY - oldMinY),
      });

      // Sync handle count: add missing handles or remove excess ones
      const currentCount = handlesRef.current.length;
      const newCount = pts.length;

      if (newCount > currentCount) {
        canvas.renderOnAddRemove = false;
        for (let i = currentCount; i < newCount; i++) {
          const h = makeHandle(i);
          canvas.add(h);
          handlesRef.current.push(h);
        }
        canvas.renderOnAddRemove = true;
      } else if (newCount < currentCount) {
        const removed = handlesRef.current.splice(newCount);
        canvas.remove(...removed);
      }

      // Update undo snapshot so next drag starts from the current state
      oldPointsRef.current = pts.map(p => ({ x: p.x, y: p.y }));

      positionHandlesFromPolygon(polygon, handlesRef.current);
      polygon.setCoords();
      canvas.requestRenderAll();
    };
    polygon.on('modified', handlePolygonModified);
    polygonModifiedListenerRef.current = handlePolygonModified;

    polygon.setCoords();
    canvas.requestRenderAll();
  }, [canvasRef, commandStack, exitEditMode, snapSettingsRef]);

  return {
    editingPolygonRef,
    enterEditMode,
    exitEditMode,
    refreshHandles,
    /** True when vertex edit mode is active */
    isInEditMode: () => editingPolygonRef.current !== null,
    isVertexHandle,
  };
}
